"use client"

import * as React from "react"

import { useActiveBranchSlug, useActiveWorkspaceSlug } from "@/features/auth"
import { useTenantAuthFlow } from "@/features/auth/providers/tenant-auth-flow-provider"
import { buildReportsRouteScope } from "@/features/reports/utils/reporting-scope"
import type { ReportsRouteScope } from "@/features/reports/types/reports-types"

/**
 * Wrapped so "no provider" (undefined) is distinguishable from "a provider whose scope is not resolved yet"
 * (`{ scope: null }`). Without the wrapper, `override ?? routeScope` silently falls back to the URL-derived
 * BRANCH scope while a consolidated page is still loading -- a workspace-titled report quietly showing one
 * branch, which is precisely the failure this surface exists to prevent.
 */
const ReportsRouteScopeOverrideContext = React.createContext<{ scope: ReportsRouteScope | null } | undefined>(
  undefined,
)

/**
 * Run everything inside at an explicitly supplied reporting scope instead of the one derived from the URL.
 *
 * Needed because the route cannot always express the scope: Global Finance lives at the WORKSPACE root with no
 * branch slug, so `buildReportsRouteScope` — which requires one — returns null there. Rather than teach every
 * report hook about Global Finance, the surface declares its scope once and the hooks keep reading from one
 * place.
 */
export function ReportsRouteScopeOverrideProvider({
  scope,
  children,
}: {
  scope: ReportsRouteScope | null
  children: React.ReactNode
}) {
  const value = React.useMemo(() => ({ scope }), [scope])
  return (
    <ReportsRouteScopeOverrideContext.Provider value={value}>{children}</ReportsRouteScopeOverrideContext.Provider>
  )
}

export function useReportsRouteScope() {
  const override = React.useContext(ReportsRouteScopeOverrideContext)
  const { authMe } = useTenantAuthFlow()
  const workspaceSlug = useActiveWorkspaceSlug()
  const branchSlug = useActiveBranchSlug()

  return React.useMemo(
    () =>
      override
        ? override.scope
        : buildReportsRouteScope({
            authMe,
            workspaceSlug,
            branchSlug,
          }),
    [override, authMe, branchSlug, workspaceSlug],
  )
}
